// Tiny hash router. A screen may return a cleanup function (e.g. the booth
// stops the camera when you navigate away).

export type Cleanup = (() => void) | void;
export interface Route {
  path: string; // e.g. '/post/:id'
  render: (root: HTMLElement, params: Record<string, string>) => Cleanup | Promise<Cleanup>;
  /** requires login (redirects to /login) */
  auth?: boolean;
}

const routes: { re: RegExp; keys: string[]; r: Route }[] = [];
let cleanup: Cleanup = undefined;
let guard: (r: Route) => string | null = () => null;
let rootEl: HTMLElement;
let renderSeq = 0;

export function addRoutes(list: Route[]) {
  for (const r of list) {
    const keys: string[] = [];
    const re = r.path === '*'
      ? /$^/ // fallback route, matched explicitly below
      : new RegExp('^' + r.path.replace(/:(\w+)/g, (_, k) => { keys.push(k); return '([^/]+)'; }) + '/?$');
    routes.push({ re, keys, r });
  }
}

/** Return a hash to redirect to, or null to allow. */
export function setGuard(fn: (r: Route) => string | null) {
  guard = fn;
}

export function navigate(hash: string, replace = false) {
  const h = hash.startsWith('#') ? hash : `#${hash}`;
  if (location.hash === h) return void render();
  if (replace) {
    history.replaceState(null, '', location.pathname + location.search + h);
    void render();
  } else location.hash = h;
}

export const currentPath = () => location.hash.replace(/^#/, '').split('?')[0] || '/';
export const queryParam = (k: string) => new URLSearchParams(location.hash.split('?')[1] ?? '').get(k);

export async function render() {
  const seq = ++renderSeq;
  const path = currentPath();
  const hit = routes.find((x) => x.re.test(path)) ?? routes.find((x) => x.r.path === '*');
  if (!hit) return;
  const redirect = guard(hit.r);
  if (redirect) return navigate(redirect, true);
  const m = hit.re.exec(path);
  const params: Record<string, string> = {};
  hit.keys.forEach((k, i) => (params[k] = decodeURIComponent(m?.[i + 1] ?? '')));
  try { if (typeof cleanup === 'function') cleanup(); } catch (e) { console.error(e); }
  cleanup = undefined;
  const c = await hit.r.render(rootEl, params);
  if (seq !== renderSeq) { if (typeof c === 'function') c(); return; }
  cleanup = c;
}

export function startRouter(root: HTMLElement) {
  rootEl = root;
  addEventListener('hashchange', () => void render());
  return render();
}
